import { ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import { isNotFoundFile, FetchState, FileResponse } from 'shared/api'

import { Box, BoxProps, Chip, Slide } from '@mui/material'
import { Image as EmptyIcon } from '@mui/icons-material';

import { Loading } from 'components/controls/loading'
import { ErrorInfo } from 'components/controls/errorInfo'

type ImageFileInputProps = {
	edittable?: boolean
	state?: FetchState<FileResponse>
	onChangeFile: (file: File) => void
	alt: string
	emptyImage?: ReactElement
	errorImage?: ReactElement
} & BoxProps
export const ImageFileInput = ({
	edittable,
	state,
	onChangeFile,
	alt,
	emptyImage,
	errorImage,
	...others
}: ImageFileInputProps) => {
	const containerRef = useRef<HTMLElement>(null)
	const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [image, setImage] = useState<string | null>(null)
	const [mouseOver, setMouseOver] = useState<boolean>(false)

	useEffect(
    () => {
      if (file) {
        const reader = new FileReader()
        reader.onload = () => {
          setImage(reader.result as string)
        }
        reader.readAsDataURL(file)
      } else if (state?.error) {
				if (isNotFoundFile(state.error)) {
					setImage("empty")
				} else {
					setImage("error")
				}
      } else if (state?.data) {
        setImage(URL.createObjectURL(state.data.blob))
      } else {
        setImage("empty")
      }
    },
    [file, state]
  )
	const fileUpload = useCallback(
		() => {
			if (!inputRef.current || edittable === false) return
	    inputRef.current.click()
	  },
		[inputRef, edittable]
	)
	const onFileInputChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			if (edittable === false || !event.target.files || event.target.files.length === 0) return
			const file = event.target.files[0]
			setFile(file)
			onChangeFile(file)
			event.target.value = ''
		},
		[onChangeFile, edittable]
	)
	return (
		<Box
			{...others}
			ref={containerRef}
			textAlign="center"
			sx={{
				border: 1,
				borderColor: 'divider',
				borderRadius: 1,
				position: 'relative',
				cursor: edittable === false ? undefined : 'pointer',
				overflow: 'hidden'
			}}
			onClick={() => fileUpload()}
			onMouseEnter={() => setMouseOver(true)}
			onMouseLeave={() => setMouseOver(false)}
		>
			<input
				ref={inputRef}
				hidden
				type="file"
				accept="image/*"
				onChange={onFileInputChange}
			/>
			{ image ==="error" ? (
				<div style={{ padding: '3em' }}>
					{errorImage ?? <ErrorInfo error={state?.error}/>}
				</div>
			) : image === "empty" ? (
				<div style={{ padding: '3em' }}>
					{emptyImage ?? <EmptyIcon fontSize='large'/>}
					<div style={{ textAlign: 'center' }}>{alt}</div>
				</div>
			) : image ? (
				<img
					src={image}
					alt={alt}
					style={{
						width: '100%',
						height: '100%',
						objectFit: 'cover',
						verticalAlign: 'middle'
					}}
				/>
			) : (
				<Loading/>
			)}
			{edittable !== false && (
				<Slide
					in={mouseOver}
					timeout={300}
					direction='up'
					container={containerRef.current}
				>
					<Box
						position="absolute"
						bottom={0}
						left={0}
						right={0}
						margin={1}
						zIndex={10}
						style={{ opacity: 0.9 }}
					>
						<Chip label="クリックして変更" color="primary" size="small"/>
					</Box>
				</Slide>
			)}
		</Box>
	)
}